import {Component, Fragment, createRef, useContext} from 'react';
import {observable, computed, action, toJS, makeObservable, runInAction, autorun} from 'mobx';
import {observer, Observer} from 'mobx-react';
import {Link} from 'react-router-dom';
import {Grid, Form, Button, Message, Icon} from 'semantic-ui-react';
import {
  forEach, map, mapValues, flatMap, groupBy, find, filter, last, isMatch, isPlainObject,
  castArray, pullAllWith, chunk, times, isEqual, isEmpty, isString,
} from 'lodash';
import {
  FetchDataError, FormFragment, RadioGroupInput,
  ValueRenderer, interpolateRoute, request, withRouter
} from 'apstra-ui-common';

import {descriptionRenderer} from '../descriptionRenderer';
import generateDashboardURI from '../generateDashboardURI';
import {useQueryInvalidator} from '../../hooks/useQueryInvalidator';
import Dashboard from './Dashboard';
import DashboardDeletionModal from './DashboardDeletionModal';
import DashboardExportModal from './DashboardExportModal';
import {PredefinedDashboardIcons} from './PredefinedDashboardIcons';
import UpdatedByLabel from './UpdatedByLabel';
import IBAContext from '../IBAContext';
import DashboardCaption from './DashboardCaption';

import './DashboardDetails.less';

function calculateNewGrid(value, newColumnNumber) {
  const oldColumnNumber = value.length;
  if (oldColumnNumber > newColumnNumber) {
    times(oldColumnNumber - newColumnNumber, () => {
      const widgets = value.pop();
      last(value).push(...widgets);
    });
  } else if (newColumnNumber > oldColumnNumber) {
    times(newColumnNumber - oldColumnNumber, () => {
      value.push([]);
    });
  }
  return value;
}
const layoutPickerRenderer = new ValueRenderer({
  condition: ({name}) => name === 'grid',
  ValueInput({name, value, schema, required, disabled, onChange}) {
    return (
      <Observer>
        {() =>
          <RadioGroupInput
            name={name}
            value={value.length}
            schema={schema}
            required={required}
            disabled={disabled}
            onChange={(newColumnNumber) => onChange(calculateNewGrid([...value], newColumnNumber))}
          />
        }
      </Observer>
    );
  }
});

@withRouter
@observer
class DashboardDetailsInternal extends Component {
  static contextType = IBAContext;

  static defaultProps = {
    dashboardPropertiesSchema: [
      {
        name: 'label',
        required: true,
        schema: {title: 'Name', type: 'string'}
      },
      {
        name: 'grid',
        schema: {
          title: 'Layout',
          description: 'Dashboards may have up to three columns for widgets.',
          type: 'number',
          oneOf: [
            {const: 1, title: 'One-column'},
            {const: 2, title: 'Two-column'},
            {const: 3, title: 'Three-column'},
          ]
        }
      },
      {
        name: 'description',
        schema: {title: 'Description', type: 'string'}
      },
      {
        name: 'default',
        appearance: 'toggle',
        schema: {
          type: 'boolean',
          title: 'Default',
          description: "Default analytics dashboard will be shown on the blueprint's dashboard.",
        }
      },
    ]
  };

  @observable isDashboardDirty = false;

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    this.disposeDirtyChecker = autorun(this.checkDashboardModification);
  }

  componentWillUnmount() {
    this.disposeDirtyChecker();
  }

  @action
  submit = async ({action = 'create', redirect = true} = {}) => {
    const {blueprintId, routes} = this.context;
    const {navigate, dashboardId, invalidateDashboardCache} = this.props;
    this.currentActionInProgress = action;
    this.errors.length = 0;
    try {
      const {id: newDashboardId} = await request(
        action !== 'update' ?
          interpolateRoute(routes.dashboardList, {blueprintId})
        :
          interpolateRoute(routes.dashboardDetails, {blueprintId, dashboardId}),
        {method: action !== 'update' ? 'POST' : 'PUT', body: JSON.stringify(toJS(this.dashboard))}
      );
      await invalidateDashboardCache();
      if (redirect) {
        navigate(generateDashboardURI({blueprintId, dashboardId: newDashboardId}));
      }
    } catch (error) {
      runInAction(() => {
        this.currentActionInProgress = null;
        const {response, responseBody} = error;
        if (response && response.status === 422 && responseBody) {
          this.errors = this.processErrors(responseBody);
          this.propertiesFormRef?.current?.scrollIntoView({block: 'start', behavior: 'smooth'});
        } else {
          this.errors.push({type: 'http', error});
        }
      });
    }
  };

  @computed get dashboard() {
    return this.props.action ? this.currentDashboard : this.props.dashboard;
  }

  @computed get isDashboardValid() {
    return true;
  }

  @computed get isDashboardPredefined() {
    return !isEmpty(this.props.dashboard.predefined_dashboard);
  }

  @computed get httpError() {
    return find(this.errors, {type: 'http'});
  }

  @computed get propertyErrorMessages() {
    return mapValues(
      groupBy(filter(this.errors, {type: 'property'}), 'propertyName'),
      (errors) => map(errors, 'message')
    );
  }

  @computed get predefinedDashboard() {
    return find(this.props.predefinedDashboards, {name: this.dashboard.predefined_dashboard});
  }

  processErrors({errors}) {
    const result = [];
    function addError(messages, generateError) {
      result.push(...castArray(messages).map((message) => generateError(message)));
    }
    if (isPlainObject(errors)) {
      for (const propertyName of ['label', 'description']) {
        if (propertyName in errors) {
          addError(errors[propertyName], (message) => ({type: 'property', propertyName, message}));
        }
      }
      if (errors.grid) {
        forEach(errors.grid, (columnErrors, columnIndex) => {
          forEach(columnErrors, (widgetErrors, widgetIndex) => {
            let processedWidgetErrors = null;
            if (isPlainObject(widgetErrors)) {
              processedWidgetErrors = flatMap(widgetErrors, (propertyErrors, propertyName) => {
                if (propertyName === 'probe_id' || propertyName === 'stage_name') propertyName = 'stage';
                return castArray(propertyErrors).map((message) => ({type: 'property', propertyName, message}));
              });
            } else if (isString(widgetErrors)) {
              processedWidgetErrors = [{type: 'generic', message: widgetErrors}];
            }
            if (!isEmpty(processedWidgetErrors)) {
              result.push({
                type: 'widget',
                columnIndex: Number(columnIndex),
                widgetIndex: Number(widgetIndex),
                errors: processedWidgetErrors
              });
            }
          });
        });
      }
    }
    return result;
  }

  @action
  setDashboardAsDirty = (isDirty) => {
    this.isDashboardDirty = isDirty;
  };

  @action
  revertDashboard = () => {
    this.currentDashboard = this.getNormalizedDashboard();
    this.setDashboardAsDirty(false);
    this.errors = [];
  };

  @action
  setDashboardPropertyValue = (propertyName, value) => {
    this.dashboard[propertyName] = value;
    pullAllWith(this.errors, [{type: 'property', propertyName}], isMatch);
  };

  @action
  setDashboardDeletionModalProps = (props) => {
    this.dashboardDeletionModalProps = props;
  };

  @action
  setDashboardExportModalProps = (props) => {
    this.dashboardExportModalProps = props;
  };

  checkDashboardModification = () => {
    if (
      this.props.action === 'update' &&
      !this.isDashboardDirty &&
      !isEqual(this.dashboard.grid, this.props.dashboard.grid)
    ) {
      this.setDashboardAsDirty(true);
    }
  };

  getNormalizedDashboard = () => {
    return !this.props.dashboardId ? {
      label: '',
      description: '',
      default: false,
      grid: [[]],
    } : {
      label: this.props.action === 'clone' ? `${this.props.dashboard.label} clone` : this.props.dashboard.label,
      description: this.props.dashboard.description,
      default: this.props.dashboard.default,
      grid: toJS(this.props.dashboard.grid),
    };
  };

  @observable currentDashboard = this.getNormalizedDashboard();
  @observable.ref dashboardDeletionModalProps = null;
  @observable.ref dashboardExportModalProps = null;
  @observable currentActionInProgress = null;
  @observable errors = [];

  propertiesFormRef = createRef();

  renderBreadcrumbSections() {
    const {blueprintId} = this.context;
    const {dashboardId, action} = this.props;
    const {dashboard, predefinedDashboard} = this;
    const result = [{key: 'home', content: 'Dashboards', as: Link, to: generateDashboardURI({blueprintId})}];
    if (!dashboardId) {
      result.push({key: 'label', active: true, content: 'New Dashboard'});
    } else if (action) {
      const currentLabel = this.props.dashboard.label;
      result.push(
        {key: 'label', content: currentLabel, as: Link, to: generateDashboardURI({blueprintId, dashboardId})},
        {key: 'action', active: true, content: action === 'update' ? 'Edit' : 'Clone'}
      );
    } else {
      result.push({
        key: 'label',
        active: true,
        content: (
          <Fragment>
            {dashboard.label}
            &nbsp;
            <PredefinedDashboardIcons
              asLabel
              isDashboardInstance
              predefinedDashboard={predefinedDashboard}
            />
            &nbsp;
            <UpdatedByLabel updatedBy={dashboard.updated_by} timestamp={dashboard.updated_at} />
          </Fragment>
        )
      });
    }
    return result;
  }

  render() {
    const {
      action, dashboardId,
      dashboardPropertiesSchema,
      probes,
      refetchData,
      navigate,
    } = this.props;
    const {blueprintId} = this.context;
    const {
      dashboard,
      errors,
      dashboardDeletionModalProps,
      setDashboardDeletionModalProps,
      dashboardExportModalProps,
      setDashboardExportModalProps,
      predefinedDashboard,
    } = this;
    return (
      <Grid className='dashboard-details'>
        <DashboardCaption
          action={action}
          breadcrumbSections={this.renderBreadcrumbSections()}
          dashboard={dashboard}
          predefinedDashboard={predefinedDashboard}
          setDashboardDeletionModalProps={setDashboardDeletionModalProps}
          setDashboardExportModalProps={setDashboardExportModalProps}
        />
        {action &&
          <Grid.Row>
            <Grid.Column>
              <a ref={this.propertiesFormRef} />
              <Form className='dashboard-properties'>
                {chunk(dashboardPropertiesSchema, 2).map((schemaChunk, index) =>
                  <Form.Group key={index} widths='equal'>
                    <FormFragment
                      schema={schemaChunk}
                      values={dashboard}
                      renderers={[
                        descriptionRenderer,
                        layoutPickerRenderer,
                      ]}
                      errors={this.propertyErrorMessages}
                      disabled={!!this.currentActionInProgress}
                      onChange={this.setDashboardPropertyValue}
                    />
                  </Form.Group>
                )}
              </Form>
            </Grid.Column>
          </Grid.Row>
        }
        <Grid.Row>
          <Grid.Column>
            <Dashboard
              editable={!!action}
              dashboard={dashboard}
              errors={errors}
              probes={probes}
              refetchData={refetchData}
            />
          </Grid.Column>
        </Grid.Row>
        {this.httpError &&
          <Grid.Row>
            <Grid.Column>
              <FetchDataError error={this.httpError.error} />
            </Grid.Column>
          </Grid.Row>
        }
        {action === 'update' && this.isDashboardPredefined && this.isDashboardDirty &&
          <Grid.Row>
            <Grid.Column>
              <Message icon warning>
                <Icon name='warning sign' />
                <Message.Content>
                  <Message.Header>{'Modification of Predefined Dashboard'}</Message.Header>
                  <p>
                    {'After saving the changes, it won\'t be possible to edit '}
                    {'this dashboard as a predefined dashboard.'}
                  </p>
                  <Button
                    icon='undo'
                    content='Revert Dashboard Changes'
                    onClick={this.revertDashboard}
                  />
                </Message.Content>
              </Message>
            </Grid.Column>
          </Grid.Row>
        }
        {action &&
          <Grid.Row>
            <Grid.Column textAlign='center'>
              {dashboardId ?
                <Fragment>
                  <Button
                    primary size='large'
                    disabled={!!this.currentActionInProgress || !this.isDashboardValid}
                    loading={this.currentActionInProgress === 'update'}
                    onClick={() => this.submit({action})}
                    content={action === 'update' ? 'Update Dashboard' : 'Create Dashboard'}
                  />
                  <Button
                    className='ghost'
                    size='large'
                    disabled={!!this.currentActionInProgress}
                    as={Link}
                    to={generateDashboardURI({blueprintId, dashboardId})}
                    content='Cancel'
                  />
                </Fragment>
              :
                <Button
                  primary size='large'
                  disabled={!!this.currentActionInProgress || !this.isDashboardValid}
                  loading={this.currentActionInProgress === 'create'}
                  onClick={() => this.submit({action: 'create'})}
                  content='Create Dashboard'
                />
              }
            </Grid.Column>
          </Grid.Row>
        }
        <DashboardDeletionModal
          open={false}
          onClose={() => setDashboardDeletionModalProps({open: false})}
          onSuccess={() => navigate(generateDashboardURI({blueprintId}))}
          {...dashboardDeletionModalProps}
        />
        <DashboardExportModal
          open={false}
          onClose={() => setDashboardExportModalProps({open: false})}
          {...dashboardExportModalProps}
        />
      </Grid>
    );
  }
}

const DashboardDetails = (props) => {
  const {blueprintId} = useContext(IBAContext);
  const invalidateDashboardCache = useQueryInvalidator('dashboard', 'remove', blueprintId, props.dashboardId);
  return <DashboardDetailsInternal {...props} invalidateDashboardCache={invalidateDashboardCache} />;
};

export default DashboardDetails;
